// ─────────────────────────────────────────────
//  Navbar.js — StockIQ top navigation
//  Mobile: bottom tab bar (Dashboard/Portfolio/Watchlist/Alerts)
//          + hamburger drawer (Intel Board + user/logout)
//  Desktop: full horizontal nav (unchanged)
// ─────────────────────────────────────────────
import React, { useState, useRef, useEffect, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { searchSymbol, fromYahooSymbol } from "../config/api";
import "./Navbar.css";

function useDebounce(fn, delay) {
  const timer = useRef(null);
  return useCallback((...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

// Bottom tab config — 4 primary destinations
const BOTTOM_TABS = [
  { to: "/", end: true, icon: "📊", label: "Dashboard" },
  { to: "/portfolio", icon: "💼", label: "Portfolio" },
  { to: "/watchlist", icon: "👁", label: "Watchlist" },
  { to: "/alerts", icon: "🔔", label: "Alerts", dot: true },
  { to: "/learning", icon: "📖", label: "Learning"},
  { to: "/simulationengine", icon: "📈", label: "Simulation"}
];

// Drawer-only links (overflow)
const DRAWER_EXTRA = [
  { to: "/board", icon: "📋", label: "Intel Board" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [searchVal, setSearchVal] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [searching, setSearching] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);   // desktop avatar dropdown
  const [drawerOpen, setDrawerOpen] = useState(false);   // mobile drawer

  const dropRef = useRef(null);
  const searchRef = useRef(null);

  // Close dropdown / suggestions on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggest(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  // Search autocomplete
  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 1) { setSuggestions([]); return; }
    setSearching(true);
    try {
      const results = await searchSymbol(q);
      setSuggestions(results.slice(0, 6));
      setShowSuggest(true);
    } catch (_) {
      setSuggestions([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const debouncedSearch = useDebounce(fetchSuggestions, 350);

  function handleInputChange(e) {
    const val = e.target.value;
    setSearchVal(val);
    if (val.trim().length >= 1) debouncedSearch(val.trim());
    else { setSuggestions([]); setShowSuggest(false); }
  }

  function navigateToStock(yahooTicker) {
    const sym = fromYahooSymbol(yahooTicker);
    const exchange = yahooTicker.endsWith(".BO") ? "BSE" : "NSE";
    setSearchVal("");
    setSuggestions([]);
    setShowSuggest(false);
    navigate(`/stock/${sym}`, { state: { exchange } });
  }

  function handleSearch(e) {
    e.preventDefault();
    const sym = searchVal.trim().toUpperCase();
    if (!sym) return;
    if (suggestions.length > 0) navigateToStock(suggestions[0].symbol);
    else { setSearchVal(""); setShowSuggest(false); navigate(`/stock/${sym}`); }
  }

  async function handleLogout() {
    setDropOpen(false);
    setDrawerOpen(false);
    await logout();
    navigate("/login");
  }

  function closeDrawer() { setDrawerOpen(false); }

  const initials = user?.email ? user.email.slice(0, 2).toUpperCase() : "??";

  return (
    <>
      {/* ══════════════ TOP NAVBAR ══════════════ */}
      <nav className="siq-navbar">

        {/* Brand */}
        <NavLink to="/" className="siq-brand">
          <div className="siq-brand-icon">VQ</div>
          <span className="siq-brand-name">VeeraStock<span>IQ</span></span>
        </NavLink>

        {/* Search */}
        <div className="siq-search-wrap" ref={searchRef}>
          <form onSubmit={handleSearch}>
            <span className="siq-search-icon">{searching ? "⏳" : "🔍"}</span>
            <input
              className="siq-search-input"
              placeholder="Search NSE / BSE symbol…"
              value={searchVal}
              onChange={handleInputChange}
              onFocus={() => suggestions.length > 0 && setShowSuggest(true)}
              autoComplete="off"
            />
          </form>
          {showSuggest && suggestions.length > 0 && (
            <div className="siq-suggest-drop">
              {suggestions.map((s) => (
                <div
                  key={s.symbol}
                  className="siq-suggest-item"
                  onMouseDown={() => navigateToStock(s.symbol)}
                >
                  <div className="siq-suggest-left">
                    <span className="siq-suggest-sym">{fromYahooSymbol(s.symbol)}</span>
                    <span className="siq-suggest-name">{s.longname || s.shortname || ""}</span>
                  </div>
                  <span className="siq-suggest-exch">
                    {s.symbol.endsWith(".BO") ? "BSE" : "NSE"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Desktop nav links */}
        <ul className="siq-nav-links">
          <li><NavLink to="/" end>📊 Dashboard</NavLink></li>
          <li><NavLink to="/portfolio">💼 Portfolio</NavLink></li>
          <li><NavLink to="/watchlist">👁 Watchlist</NavLink></li>
          <li><NavLink to="/alerts">🔔 Alerts</NavLink></li>
          <li><NavLink to="/board">📋 Intel Board</NavLink></li>
          <li><NavLink to="/learning">📖 Learning</NavLink></li>
          <li><NavLink to="/simulationengine">📈 Simulation</NavLink></li>
        </ul>

        {/* Desktop user avatar */}
        <div className="siq-user-wrap" ref={dropRef}>
          <div className="siq-avatar" onClick={() => setDropOpen(p => !p)} title={user?.email}>
            {initials}
          </div>
          {dropOpen && (
            <div className="siq-dropdown">
              <div className="siq-dropdown-email">{user?.email}</div>
              <button className="siq-dropdown-item" onClick={() => { setDropOpen(false); navigate("/portfolio"); }}>💼 My Portfolio</button>
              <button className="siq-dropdown-item" onClick={() => { setDropOpen(false); navigate("/alerts"); }}>🔔 My Alerts</button>
              <button className="siq-dropdown-item danger" onClick={handleLogout}>🚪 Log Out</button>
            </div>
          )}
        </div>

        {/* Mobile hamburger — opens drawer */}
        <button
          className={`siq-hamburger ${drawerOpen ? "open" : ""}`}
          onClick={() => setDrawerOpen(p => !p)}
          aria-label="Menu"
        >
          <span /><span /><span />
        </button>
      </nav>

      {/* ══════════════ MOBILE DRAWER ══════════════ */}
      <div
        className={`siq-drawer-overlay ${drawerOpen ? "open" : ""}`}
        onClick={closeDrawer}
      />
      <div className={`siq-drawer ${drawerOpen ? "open" : ""}`}>
        {/* Drawer header */}
        <div className="siq-drawer-head">
          <NavLink to="/" className="siq-brand" onClick={closeDrawer}>
            <div className="siq-brand-icon">VQ</div>
            <span className="siq-brand-name">VeeraStock<span>IQ</span></span>
          </NavLink>
          <button className="siq-drawer-close" onClick={closeDrawer}>✕</button>
        </div>

        {/* All nav links in drawer (so user can reach everything) */}
        <ul className="siq-drawer-links">
          <li><NavLink to="/" end onClick={closeDrawer}>📊 Dashboard</NavLink></li>
          <li><NavLink to="/portfolio" onClick={closeDrawer}>💼 Portfolio</NavLink></li>
          <li><NavLink to="/watchlist" onClick={closeDrawer}>👁 Watchlist</NavLink></li>
          <li><NavLink to="/alerts" onClick={closeDrawer}>🔔 Alerts</NavLink></li>
          <li><div className="siq-drawer-divider" /></li>
          <li><NavLink to="/board" onClick={closeDrawer}>📋 Intel Board</NavLink></li>
          <li><NavLink to="/learning" onClick={closeDrawer}>📖 Learning</NavLink></li>
          <li><NavLink to="/simulationengine" onClick={closeDrawer}>📈 Simulation</NavLink></li>
        </ul>

        {/* User section at bottom of drawer */}
        <div className="siq-drawer-user">
          <div className="siq-avatar">{initials}</div>
          <div className="siq-drawer-user-info">
            <div className="siq-drawer-user-email">{user?.email}</div>
          </div>
          <button className="siq-drawer-logout" onClick={handleLogout}>Log out</button>
        </div>
      </div>

      {/* ══════════════ BOTTOM TAB BAR ══════════════ */}
      <nav className="siq-bottom-tabs">
        <div className="siq-bottom-tabs-inner">
          {BOTTOM_TABS.map(tab => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => `siq-tab-item ${isActive ? "active" : ""}`}
            >
              <span className="siq-tab-icon">{tab.icon}</span>
              <span className="siq-tab-label">{tab.label}</span>
              {tab.dot && <span className="siq-tab-alert-dot" />}
            </NavLink>
          ))}
        </div>
      </nav>
    </>
  );
}